import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { hideBootSplash, hideBootSplashIfNoLoader, LOADER_ATTR } from '../../utils/bootSplash';

interface LoaderProps {
    backgroundTransparent?: boolean;
    loadingText?: string;
    showBackButton?: boolean;
    backLocation?: string;
    overlay?: boolean;
}

const BRAND  = '#cbb26a';

// Class carried by BackHeaderBar. A page-level Loader leaves it uncovered so the title and back
// button stay on screen while the content loads, instead of the whole page going white.
export const PAGE_HEADER_CLASS = 'app-page-header';

const pageHeaderBottom = (): number => {
    const el = document.querySelector(`.${PAGE_HEADER_CLASS}`);
    if (!el) return 0;
    const r = el.getBoundingClientRect();
    // Only a header pinned at the top of the viewport counts (not one scrolled away).
    return r.top <= 1 && r.bottom > 0 && r.bottom < 200 ? Math.round(r.bottom) : 0;
};

const Loader: React.FC<LoaderProps> = ({
    backgroundTransparent = false,
    loadingText = 'Loading...',
    overlay = true,
}) => {
    const imgRef = useRef<HTMLImageElement>(null);
    // Hand off from the boot splash only once this logo is actually painted.
    // Measured before first paint so the loader never flashes over the header.
    const [topInset, setTopInset] = useState(0);
    useLayoutEffect(() => {
        if (overlay && !backgroundTransparent) setTopInset(pageHeaderBottom());
    }, [overlay, backgroundTransparent]);
    const onLogoReady = useCallback(() => requestAnimationFrame(hideBootSplash), []);
    useEffect(() => {
        if (imgRef.current?.complete) onLogoReady();
        // Unmounting without a successor Loader (page content took over) — drop the splash too.
        return hideBootSplashIfNoLoader;
    }, [onLogoReady]);

    const content = (
    <>
        <style>{`
            /* No mount/entry animation on purpose: a cold start hands off boot splash
               (public/index.html) → route Suspense fallback → page's own loading state,
               each a fresh <Loader>. An entry pop replayed on every hand-off reads as a
               second logo appearing on slow networks; static, identically placed
               logo + text make the hand-offs invisible. */
            /* Waterline: shield + house stay still — only the wave band at the base catches light */
            @keyframes _ld_wave_sweep {
                0%   { background-position: 120% 0; }
                100% { background-position: -120% 0; }
            }

            /* Gleam layer, clipped to (logo silhouette ∩ bottom wave band) so only the wave catches light */
            ._ld_shine {
                position: absolute;
                inset: 0;
                -webkit-mask-image: url(/logo-icon.png), linear-gradient(to bottom, transparent 0 58%, #000 64% 100%);
                mask-image: url(/logo-icon.png), linear-gradient(to bottom, transparent 0 58%, #000 64% 100%);
                -webkit-mask-size: contain, 100% 100%;
                mask-size: contain, 100% 100%;
                -webkit-mask-repeat: no-repeat, no-repeat;
                mask-repeat: no-repeat, no-repeat;
                -webkit-mask-position: center, center;
                mask-position: center, center;
                -webkit-mask-composite: source-in;
                mask-composite: intersect;
                background: linear-gradient(115deg, transparent 35%, rgba(255,255,255,0.95) 50%, transparent 65%);
                background-size: 260% 100%;
                mix-blend-mode: overlay;
                animation: _ld_wave_sweep 0.8s ease-in-out infinite;
                pointer-events: none;
            }
            @media (prefers-reduced-motion: reduce) {
                ._ld_shine { animation: none !important; }
            }
        `}</style>

        <div {...{ [LOADER_ATTR]: '' }} style={{
            position: overlay ? 'fixed' : 'relative',
            inset: overlay ? `${topInset}px 0 0 0` : 'auto',
            boxSizing: 'border-box',
            width: overlay ? '100vw' : '100%',
            height: overlay ? `calc(100dvh - ${topInset}px)` : undefined,
            minHeight: overlay ? `calc(100vh - ${topInset}px)` : 280,
            background: backgroundTransparent ? 'rgba(255,255,255,0.96)' : '#ffffff',
            backdropFilter: backgroundTransparent ? 'blur(10px)' : undefined,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: overlay ? 9999 : undefined,
        }}>

            {/* ── Logo — shield & house stay still, the wave at the base gleams ── */}
            <div style={{ position: 'relative', width: 110, height: 110, marginBottom: 20 }}>
                <img
                    ref={imgRef}
                    onLoad={onLogoReady}
                    onError={onLogoReady}
                    src="/logo-icon.png"
                    alt="MyCasa"
                    style={{
                        width: '100%', height: '100%',
                        objectFit: 'contain',
                        userSelect: 'none',
                        pointerEvents: 'none',
                        filter: `drop-shadow(0 2px 12px ${BRAND}80)`,
                    }}
                />
                <div className="_ld_shine" />
            </div>

            {/* ── Loading text ── */}
            {loadingText && (
                <p
                    style={{
                        margin: 0,
                        fontSize: 14,
                        lineHeight: 1.2,
                        fontWeight: 600,
                        color: '#8a7440',
                        letterSpacing: '0.3px',
                        textAlign: 'center',
                    }}
                >
                    {loadingText}
                </p>
            )}
        </div>
    </>
    );

    // Portal the full-screen overlay to <body> so `position: fixed` is always
    // relative to the viewport — a transformed/filtered ancestor would otherwise
    // become its containing block and push the loader off-centre.
    return overlay && typeof document !== 'undefined' ? createPortal(content, document.body) : content;
};

export default Loader;
